import React, { useCallback, type FC } from 'react'
import { useIntl } from 'react-intl'
import { useRecoilCallback, useRecoilState } from 'recoil'

import {
  BUTTON_VARIANT_PRIMARY,
  Button,
  ContextMenu,
  Flex,
  IconArrow1Down,
  SPACING_4,
  SpacingVertical,
  Text,
  makeButtonWithMenu,
} from 'mailup-web-components'

import { selectionState } from 'Pages/GroupsList/recoil/state/state'
import type { GroupOperation } from '../model'
import { modalDeleteOpenCallback } from '../recoil/callbacks/modals'

const MenuComponent: FC = () => {
  const intl = useIntl()

  const [selectedGroups, setSelectedGroups] = useRecoilState(selectionState)
  const modalDeleteOpenCbk = useRecoilCallback(modalDeleteOpenCallback, [])
  const modalDeleteOpen = useCallback(
    (op: GroupOperation) => modalDeleteOpenCbk({ groups: selectedGroups, op }),
    [selectedGroups, modalDeleteOpenCbk],
  )

  const actions = [
    {
      label: intl.formatMessage({ id: 'Load.GroupsList.ActionDeleteGroups' }),
      onClick: () => modalDeleteOpen('DeleteGroup'),
      variant: 'danger',
    },
    {
      label: intl.formatMessage({
        id: 'Load.GroupsList.ActionDeleteRecipients',
      }),
      onClick: () => modalDeleteOpen('DeleteRecipients'),
      variant: 'danger',
    },
    {
      label: intl.formatMessage({
        id: 'Load.GroupsList.ActionDeleteGroupsAndRecipients',
      }),
      onClick: () => modalDeleteOpen('DeleteGroupAndRecipients'),
      variant: 'danger',
    },
    {
      label: intl.formatMessage({
        id: 'Load.GroupsList.ActionEmptyGroups',
      }),
      onClick: () => modalDeleteOpen('EmptyGroup'),
      variant: 'danger',
    },
    {
      label: intl.formatMessage({ id: 'Load.Common.CancelSelection' }),
      onClick: (): void => {
        setSelectedGroups([])
      },
      separator: true,
      'data-mu-test': 'GroupsList_SelectedGroupsMenu_discard',
    },
  ]

  return <ContextMenu size="md" items={actions} />
}

const ButtonComponent: FC = () => {
  const intl = useIntl()

  const [selectedGroups] = useRecoilState(selectionState)

  return (
    <Button
      kind={BUTTON_VARIANT_PRIMARY}
      disabled={selectedGroups.length === 0}
    >
      <Flex alignItems="center" gap="8px">
        {intl.formatMessage({ id: 'Load.MailupCommon.Actions' })}
        <IconArrow1Down />
      </Flex>
    </Button>
  )
}

const BatchActionsButton = makeButtonWithMenu(ButtonComponent, MenuComponent)

const BatchActions: FC = () => {
  const intl = useIntl()

  const [selectedGroups] = useRecoilState(selectionState)

  return (
    <>
      <Flex alignItems="center" justifyContent="start" gap="24px">
        <BatchActionsButton />
        <Text>
          {selectedGroups.length > 0
            ? `${intl.formatMessage({ id: 'Load.GroupsList.SelectedGroups' })} ${selectedGroups.length}`
            : ''}
        </Text>
      </Flex>
      <SpacingVertical variant={SPACING_4} />
    </>
  )
}

export default BatchActions
